import { convertToNumberIfPureNumber } from '../../core/utils/string-utils.js';

/**
 * Normalizes an unknown cell value for the `date` and `datetime` column types
 * to a valid `Date` or `null`.
 *
 * - `null` / `undefined` → `null`
 * - `Date` instance → returned as-is (or `null` if invalid)
 * - number (epoch ms) → `new Date(value)`
 * - numeric string `"1593982219000"` → converted to number first, then `new Date`
 * - ISO 8601 string `"2022-10-29T22:33:22Z"` → `new Date(value)`
 * - any other value → stringified, then treated like a string
 * - unparseable value → `null`
 *
 * @param value - The raw cell value.
 * @returns A `Date` instance, or `null` if the value cannot be parsed.
 * @internal
 */
export function coerceDatetimeCellValue(value: unknown): Date | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  let coercedValue: string | number;
  if (typeof value === 'number') {
    coercedValue = value;
  } else {
    // Numeric strings must become numbers before `new Date`, otherwise bare
    // digits are parsed as a year ("0" → 2000) instead of epoch ms.
    const valueAsString = typeof value === 'string' ? value : String(value);
    if (valueAsString.trim() === '') {
      return null;
    }
    coercedValue = convertToNumberIfPureNumber(valueAsString);
  }

  const date = new Date(coercedValue);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Normalizes an unknown cell value for the `long` column type to a
 * `string`, `number`, `bigint`, or `null`.
 *
 * Strings and bigints are passed through as-is because large integer values
 * (exceeding `Number.MAX_SAFE_INTEGER`) are often serialized as strings by APIs,
 * and `formatLong` from `@dynatrace-sdk/units` accepts all three types.
 *
 * - `null` / `undefined` → `null`
 * - `number` → the number, or `null` if `NaN` or infinite
 * - `bigint` → the bigint as-is
 * - `string` → the string as-is
 * - any other value → `null`
 *
 * @param value - The raw cell value.
 * @returns The coerced value, or `null` if the value is not a valid long.
 * @internal
 */
export function coerceLongCellValue(
  value: unknown,
): string | number | bigint | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'bigint' || typeof value === 'string') {
    return value;
  }
  return null;
}

/**
 * Normalizes an unknown cell value for the `number`, `bit`, and `currency`
 * column types to a finite `number` or `null`.
 *
 * Only `number` and `string` inputs are coerced; all other types return `null`
 * to avoid silent conversions (e.g. `Date` → epoch ms, `true` → `1`, `[]` → `0`).
 *
 * - `null` / `undefined` → `null`
 * - `number` → the number, or `null` if `NaN` or infinite
 * - numeric string `"42"`, `"3.14"` → converted to number
 * - empty or whitespace-only string → `null`
 * - non-numeric string `"abc"` → `null`
 * - any other value, including `Date`, `boolean`, `bigint` and arrays → `null`
 *
 * @param value - The raw cell value.
 * @returns A finite number, or `null` if the value cannot be coerced.
 * @internal
 */
export function coerceNumericCellValue(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const valueAsNumber = Number(value);
  return Number.isFinite(valueAsNumber) ? valueAsNumber : null;
}
